"use client"

import { useTheme } from "next-themes"
import { useCallback } from "react"

/**
 * Hook para animação de transição de tema usando View Transition API
 * Cria um efeito de "ripple" circular que expande do ponto clicado
 *
 * @returns Função para mudar tema com animação
 */
export function useThemeAnimation() {
  const { setTheme } = useTheme()

  const setThemeWithAnimation = useCallback(
    async (
      theme: string,
      event?: React.MouseEvent<Element>
    ) => {
      // Verificar se View Transition API está disponível
      if (
        typeof document === "undefined" ||
        !document.startViewTransition
      ) {
        // Fallback sem animação
        setTheme(theme)
        return
      }

      // Se não tem evento, muda sem animação
      if (!event) {
        setTheme(theme)
        return
      }

      // OPÇÃO 1: Usar centro da tela (ATUAL)
      // const x = window.innerWidth / 2
      // const y = window.innerHeight / 2

      // OPÇÃO 2: Usar coordenadas do clique (comentado)
      const x = event.clientX
      const y = event.clientY

      // Debug: verificar se as coordenadas estão corretas
      console.log('🎯 Coordenadas da animação:', { x, y, origem: 'centro da tela' })

      // Calcular o raio máximo necessário para cobrir toda a tela
      // partindo do ponto clicado
      const endRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      )

      // Criar a transição com callback síncrono
      const transition = document.startViewTransition(() => {
        setTheme(theme)
      })

      // Aguardar a transição estar pronta
      try {
        await transition.ready

        // Animar usando clip-path circular no pseudo-elemento ::view-transition-new(root)
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${endRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: 800, // Aumentado para 800ms (era 500ms)
            easing: "ease-in-out",
            pseudoElement: "::view-transition-new(root)",
          }
        )
      } catch (error) {
        console.error('Erro na animação de tema:', error)
      }
    },
    [setTheme]
  )

  return { setThemeWithAnimation }
}
